import { RudderAnalytics } from '@rudderstack/analytics-js';
import hasIn from 'lodash/hasIn';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import store from '@/store';
import { getFlagsmithEnabledFlags } from '@/lib/flagsmith-helper';

export const isTestingEmail = (email) => {
  // By default we don't want to track knack emails
  if (process.env.VUE_APP_ANALYTICS_TRACK_KNACK_EMAILS !== 'true') {
    const isKnackEmail = email.includes('@knack.com') || email.includes('@knackhq.com');
    if (isKnackEmail) {
      return true;
    }
  }

  const testEmailPrefixes = [
    'dev+appbuilder',
    'dev+cypress-',
    'dev+apiwrappertests-',
    'dev+benchmarktests-',
    'dev+playwright-',
    'dev.playwright.',
  ];

  return testEmailPrefixes.some((testEmailPrefix) => email.startsWith(testEmailPrefix));
};

const isRudderStackEnabled = () => !!process.env.VUE_APP_RUDDERSTACK_WRITE_KEY
  && !!process.env.VUE_APP_RUDDERSTACK_DATA_PLANE_URL
  && (!hasIn(window, 'Cypress') || store.state.app.settings.enableSegment);

export const shouldSendAnalytics = (email) => isRudderStackEnabled() && !!email && !isTestingEmail(email);

// Throttle non-critical events to reduce redundant calls
// Based on analytics best practices for different event types
export const shouldThrottleEvent = (eventName, groupId, throttleSeconds = 3) => {
  try {
    const storageKey = `analytics_throttle_${eventName}_${groupId}`;
    const lastSent = localStorage.getItem(storageKey);
    const now = Date.now();

    if (!lastSent || now - parseInt(lastSent, 10) > throttleSeconds * 1000) {
      localStorage.setItem(storageKey, now.toString());
      return false; // Don't throttle
    }
    return true; // Throttle this event
  } catch (error) {
    // If localStorage fails, don't throttle
    return false;
  }
};

// Check if session-based event was already sent to avoid duplicates
export const wasSessionEventSent = (eventName) => {
  try {
    return sessionStorage.getItem(`session_event_${eventName}`) === 'true';
  } catch (error) {
    return false;
  }
};

// Mark session-based event as sent
export const markSessionEventSent = (eventName) => {
  try {
    sessionStorage.setItem(`session_event_${eventName}`, 'true');
  } catch (error) {
    // Silently handle sessionStorage errors
  }
};

const rudderAnalytics = new RudderAnalytics();

window.isRudderStackLoaded = false;

const loadRudderStack = async () => {
  if (window.isRudderStackLoaded || !isRudderStackEnabled() || isNil(rudderAnalytics.load)) {
    return;
  }

  rudderAnalytics.load(
    process.env.VUE_APP_RUDDERSTACK_WRITE_KEY,
    process.env.VUE_APP_RUDDERSTACK_DATA_PLANE_URL,
    {},
  );

  window.isRudderStackLoaded = true;

  const account = store.state.app.get('account');

  const appName = store.state.app.get('name');

  const appSlug = store.state.app.get('slug');

  const planName = get(account, 'product_plan.name', 'Trial');

  const monthlySpend = get(account, 'product_plan.price', 0);

  const planRecords = get(account, 'product_plan.records');

  const planApps = get(account, 'product_plan.apps');

  const accountSlug = get(account, 'slug');

  if (isTestingEmail(Knack.Api.email)) return;

  const accountMeta = {
    name: account.name,
    plan: planName,
    monthly_spend: monthlySpend,
    plan_records: planRecords,
    plan_apps: planApps,
    referral: account.referral,
  };

  const meta = {
    email: Knack.Api.email,
    name: `${Knack.Api.first_name} ${Knack.Api.last_name}`,
    company: {
      id: Knack.Api.account_id,
      ...accountMeta,
    },
    app_slug: appSlug,
    app_name: appName,
    v3_account_slug: accountSlug,
    plan: planName,
    traits: {
      email: Knack.Api.email,
      plan: planName,
      monthly_spend: monthlySpend,
      plan_records: planRecords,
      plan_apps: planApps,
    },
  };

  await new Promise((resolve) => {
    rudderAnalytics.identify(Knack.Api.user_id, meta, async () => {
      rudderAnalytics.group(Knack.Api.account_id, {
        ...accountMeta,

        // Add flagsmith_ prefix to all flag names to avoid confusion
        ...Object.fromEntries(
          Object.entries(await getFlagsmithEnabledFlags())
            .map(([k, v]) => [`flagsmith_${k}`, v]),
        ),
      }, () => resolve());
    });
  });
};

const trackEvent = async (event, metadata) => {
  if (!isRudderStackEnabled() || !store.state.app || !store.state.app.get || isTestingEmail(Knack.Api.email)) {
    return;
  }

  await loadRudderStack();

  const account = store.state.app.get('account');

  const extendedMetadata = {
    groupId: Knack.Api.account_id,
    account_id: Knack.Api.account_id,
    application_id: Knack.Api.application_id,
    appSlug: store.state.app.get('slug'),
    userId: Knack.Api.user_id,
    isSharedBuilder: account?.user_id !== Knack.Api.user_id,
    builderVersion: 'v3',
    origin: 'manual', // Default origin, the AI Builder won't be used in v3
    plan: get(account, 'product_plan.name', 'Trial'),
    monthly_spend: get(account, 'product_plan.price', 0),
    ...metadata,
  };

  if (hasIn(rudderAnalytics, 'track')) {
    rudderAnalytics.track(event, extendedMetadata);
  }
};

// Track main business events without throttling
const trackMainEvent = async (event, metadata = {}) => {
  if (shouldSendAnalytics(Knack.Api.email)) {
    // Clone metadata and remove unneeded field before sending it to rudderstack (email property)
    const sanitizedMetadata = { ...metadata };
    delete sanitizedMetadata.email;
    await trackEvent(event, sanitizedMetadata);
  }
};

// Track secondary events with throttling to reduce volume
const trackSecondaryEvent = async (event, metadata = {}, throttleSeconds = 3) => {
  const groupId = (metadata.groupId || metadata.account_id || Knack.Api.account_id || 'unknown');

  if (
    !shouldThrottleEvent(event, groupId, throttleSeconds)
    && shouldSendAnalytics(Knack.Api.email)
  ) {
    // Clone metadata and remove unneeded field before sending it to rudderstack (email property)
    const sanitizedMetadata = { ...metadata };
    delete sanitizedMetadata.email;
    await trackEvent(event, sanitizedMetadata);
  }
};

// Specific event tracking functions based on analytics best practices
// UI Clicks/Actions: 1-3s throttle for rapid clicking prevention
const trackUIInteraction = async (event, metadata = {}) => {
  await trackSecondaryEvent(event, metadata, 2); // 2 seconds for UI interactions
};

// Component Impressions: 30-120s throttle for modal/tooltip views
const trackComponentImpression = async (event, metadata) => {
  await trackSecondaryEvent(event, metadata, 60); // 60 seconds for component impressions
};

// Feature Access: Session-based or 5-15 min for feature usage
const trackFeatureAccess = async (event, metadata = {}) => {
  // For feature access, use session-based deduplication first
  const sessionKey = `${event}_${metadata.groupId || metadata.account_id || Knack.Api.account_id}`;
  if (!wasSessionEventSent(sessionKey)) {
    await trackMainEvent(event, metadata);
    markSessionEventSent(sessionKey);
  }
};

// Define critical pages that should always be tracked with minimal throttling
const CRITICAL_PAGE_PATTERNS = [
  /^\/$/, // Root/home
  /^\/home/, // Home page
  /^\/welcome/, // Welcome page
  /^\/loading/, // Loading page
  /^\/schema$/, // Schema main page
  /^\/pages$/, // Pages main page
  /^\/records$/, // Records main page
  /^\/settings$/, // Settings main page
  /^\/flows$/, // Flows main page
];

// Define page patterns that should have longer throttling
const SECONDARY_PAGE_PATTERNS = [
  /^\/schema\/list\/objects\/[^/]+$/, // Object detail pages
  /^\/pages\/[^/]+$/, // Individual page views
  /^\/records\/objects\/[^/]+$/, // Records for specific objects
  /^\/settings\/[^/]+$/, // Settings subsections
];

// Define page patterns that should be heavily throttled or skipped
const LOW_PRIORITY_PAGE_PATTERNS = [
  /^\/schema\/list\/objects\/[^/]+\/fields\/[^/]+/, // Field detail pages
  /^\/pages\/[^/]+\/views\/[^/]+/, // View detail pages
  /^\/records\/objects\/[^/]+\/records\/[^/]+/, // Individual record pages
  /^\/settings\/[^/]+\/[^/]+/, // Deep settings pages
];

const getPageTrackingStrategy = (path) => {
  if (CRITICAL_PAGE_PATTERNS.some((pattern) => pattern.test(path))) {
    return { strategy: 'critical', throttleSeconds: 2 };
  }

  if (SECONDARY_PAGE_PATTERNS.some((pattern) => pattern.test(path))) {
    return { strategy: 'secondary', throttleSeconds: 10 };
  }

  if (LOW_PRIORITY_PAGE_PATTERNS.some((pattern) => pattern.test(path))) {
    return { strategy: 'lowPriority', throttleSeconds: 60 };
  }

  return { strategy: 'default', throttleSeconds: 15 };
};

const trackPage = async (routePath = null) => {
  if (!isRudderStackEnabled() || !store.state.app || !store.state.app.get || isTestingEmail(Knack.Api.email)) {
    return;
  }

  const currentPath = routePath || (window.location.pathname.split('/').slice(3).join('/') || '/');
  const { strategy, throttleSeconds } = getPageTrackingStrategy(currentPath);

  const groupId = Knack.Api.account_id || 'unknown';
  const eventKey = `page_${strategy}`;

  if (shouldThrottleEvent(eventKey, groupId, throttleSeconds)) {
    return;
  }

  await loadRudderStack();

  if (hasIn(rudderAnalytics, 'page')) {
    rudderAnalytics.page(null, {
      page_strategy: strategy,
      app_section: currentPath.split('/').filter(Boolean)[0] || 'root',
    });
  }
};

export {
  trackPage,
  trackEvent,
  trackMainEvent,
  trackSecondaryEvent,
  trackUIInteraction,
  trackComponentImpression,
  trackFeatureAccess,
};
